import { DataTypes as DT, FilterOperations as FO } from 'business/definitions';
import { type AlertFilter } from 'lib/alert/models';
import { isEmpty } from 'lodash';

/**
 * Operators that stay valid without a value. `is_null`/`is_not_null` never carry one;
 * `equal`/`not_equal` with an empty value encode "is empty"/"is not empty" and must not be dropped.
 */
export const allowsEmptyValue = (
    operation: FilterOperationsT | DynamicFilterOperationsT,
) => [FO.is_null, FO.is_not_null, FO.equal, FO.not_equal].includes(operation);

export const convertFilterOperatorForNullValue = <
    T extends Pick<QueryFilterT, 'operation' | 'value'>,
>(
    filter: T & { type?: DataTypesT | null },
): T => {
    const hasNullOperator = [FO.is_null, FO.is_not_null].includes(
        filter.operation,
    );
    if (hasNullOperator) {
        return {
            ...filter,
            operation:
                filter.operation === FO.is_null ? FO.equal : FO.not_equal,
            value: null,
        };
    }
    return filter;
};

function convertToQueryFilter(
    filter: SentenceFilterT | ExploreFilterT | QueryFilterT,
): QueryFilterT {
    const newFilter = convertFilterOperatorForNullValue(filter);
    return {
        field: filter.field,
        operation: newFilter.operation,
        value: newFilter.value,
        valueScale: filter.valueScale ?? null,
    };
}

/**
 * Transform sentence filters to query filters.
 * We need this as an independent function since filters are used in both dashboards
 * and explore.
 */
export function convertToQueryFilters<T extends QueryFilterT>(
    filters: T[],
): QueryFilterT[] {
    return filters
        .filter((f) => allowsEmptyValue(f.operation) || f.value)
        .map((f) => convertToQueryFilter(f));
}

/**
 * Transform sentence filters to explore filters.
 * We need this as an independent function since filters are used in both dashboards
 * and explore.
 */
export function sentenceToExploreFilters(
    filters: SentenceFilterT[],
): ExploreFilterT[] {
    return filters.map((f) => ({
        ...convertFilterOperatorForNullValue(f),
        type: f.type,
    }));
}

export function isEmptyFilter(filter: SentenceFilterT): boolean {
    if ([DT.date, DT.datetime].includes(filter.type)) {
        if ([FO.is_null, FO.is_not_null].includes(filter.operation)) {
            return false;
        }
    }
    return filter.value == null || filter.value === '';
}

export const isFreeTextField = (
    operation: FilterOperationsT | DynamicFilterOperationsT,
) => [FO.contains, FO.not_contains].includes(operation);

export const isOperationNull = (
    operation: FilterOperationsT | DynamicFilterOperationsT,
) => [FO.is_null, FO.is_not_null].includes(operation);

/**
 * Basically the inverse of `convertFilterOperatorForNullValue`:
 * Convert the filter that contains an operator `equal` or `not_equal` with a null value
 * into the proper frontend filter opertor `is_null` and `is_not_null`.
 * @param {AlertFilter} filter - The filter to convert.
 */
export const convertBackendFilterOperatorToFrontFilterOperator = (
    filter: AlertFilter,
): AlertFilter => {
    if (
        [FO.equal, FO.not_equal].includes(filter.operation) &&
        isEmpty(filter.value)
    ) {
        return {
            ...filter,
            operation:
                filter.operation === FO.equal ? FO.is_null : FO.is_not_null,
            value: null,
        };
    }
    return filter;
};
